import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { AddressFields, FIELD_CLASS, type AddressValue } from '@/components/properties/AddressFields';
import { PartyPicker } from '@/components/parties/PartyPicker';
import {
  DuplicateRelationshipError,
  useCreateLegalEntity,
  useLegalEntity,
  useLinkPartyRelationship,
  usePartyRelationshipRoles,
  useUnlinkPartyRelationship,
  useUpdateLegalEntity,
  type LegalEntity,
} from '@/routes/parties/useParties';

const ENTITY_TYPES = ['Company', 'Trust', 'Estate', 'Llc', 'Hoa', 'Nonprofit', 'Government', 'Other'] as const;

/**
 * Create or edit a LegalEntity (M1-25) — only `legalName` required, everything
 * else optional (the story's own "as little as possible to fill out"). In
 * edit mode also carries the "Related People" section: link a Person with a
 * role, "from the entity page" (the AC's own words) — there is no separate
 * routed page for an entity in this minimal launch UI, so this dialog IS that
 * page.
 *
 * `initialLegalName` prefills from whatever the caller's picker typed and
 * found nothing for — the common path into "create new" is typing a name
 * that doesn't exist yet, not starting from a blank form.
 */
export function LegalEntityDialog({
  entityPartyId,
  initialLegalName,
  onClose,
  onSaved,
}: {
  entityPartyId?: string;
  initialLegalName?: string;
  onClose: () => void;
  onSaved: (entity: LegalEntity) => void;
}) {
  const { t } = useTranslation();
  const isEditing = entityPartyId !== undefined;
  const existing = useLegalEntity(entityPartyId);
  const createEntity = useCreateLegalEntity();
  const updateEntity = useUpdateLegalEntity(entityPartyId ?? '');
  const roles = usePartyRelationshipRoles();
  const linkRelationship = useLinkPartyRelationship(entityPartyId ?? '');
  const unlinkRelationship = useUnlinkPartyRelationship(entityPartyId ?? '');

  const record = existing.data;
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>('Company');
  const [legalName, setLegalName] = useState(initialLegalName ?? '');
  const [dbaName, setDbaName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [clearTaxId, setClearTaxId] = useState(false);
  const [notes, setNotes] = useState('');
  const [mailingAddress, setMailingAddress] = useState<AddressValue>({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
  });
  const [hydrated, setHydrated] = useState(false);
  const [relationshipRoleId, setRelationshipRoleId] = useState('');
  const [relationshipError, setRelationshipError] = useState('');

  // One-time hydration once the existing record loads — an uncontrolled
  // re-sync on every render would stomp on whatever the person is typing.
  if (isEditing && record && !hydrated) {
    setEntityType(record.entityType as (typeof ENTITY_TYPES)[number]);
    setLegalName(record.legalName);
    setDbaName(record.dbaName ?? '');
    setNotes(record.notes ?? '');
    if (record.mailingAddress) {
      setMailingAddress({
        line1: record.mailingAddress.line1,
        line2: record.mailingAddress.line2 ?? '',
        city: record.mailingAddress.city,
        state: record.mailingAddress.state,
        postalCode: record.mailingAddress.postalCode,
      });
    }
    setHydrated(true);
  }

  const saving = createEntity.isPending || updateEntity.isPending;
  const canSubmit = legalName.trim() !== '' && (!isEditing || hydrated);

  function submit() {
    // Optional as a whole (the AC's own word) — filled in only once a street
    // line exists; a stray city with no line1 is not a usable mailing address.
    const address = mailingAddress.line1.trim()
      ? {
          line1: mailingAddress.line1.trim(),
          line2: mailingAddress.line2.trim() || null,
          city: mailingAddress.city.trim(),
          state: mailingAddress.state.trim(),
          postalCode: mailingAddress.postalCode.trim(),
        }
      : null;

    const shared = {
      entityType,
      legalName: legalName.trim(),
      dbaName: dbaName.trim() || null,
      notes: notes.trim() || null,
      mailingAddress: address,
    };

    if (isEditing) {
      updateEntity.mutate(
        { ...shared, taxId: taxId.trim() || null, clearTaxId },
        { onSuccess: (updated) => onSaved(updated) },
      );
      return;
    }

    createEntity.mutate(
      { ...shared, taxId: taxId.trim() || null },
      { onSuccess: (created) => onSaved(created) },
    );
  }

  return (
    <Dialog
      titleId="legal-entity-dialog-title"
      title={isEditing ? t('legalEntity.editTitle') : t('legalEntity.createTitle')}
      onClose={onClose}
      testId="legal-entity-dialog"
      dismissLabel={t('common.cancel')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" size="sm" disabled={!canSubmit || saving} onClick={submit}>
            {saving ? t('legalEntity.saving') : t('legalEntity.save')}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('legalEntity.entityType')}</span>
        <select
          className={FIELD_CLASS}
          value={entityType}
          onChange={(event) => setEntityType(event.target.value as (typeof ENTITY_TYPES)[number])}
        >
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`legalEntity.entityTypes.${type}`, { defaultValue: type })}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('legalEntity.legalName')}</span>
        <input
          required
          className={FIELD_CLASS}
          value={legalName}
          onChange={(event) => setLegalName(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('legalEntity.dbaName')}</span>
        <input className={FIELD_CLASS} value={dbaName} onChange={(event) => setDbaName(event.target.value)} />
      </label>

      <div className="space-y-3">
        <span className="block text-sm font-medium">{t('legalEntity.mailingAddress')}</span>
        <AddressFields
          value={mailingAddress}
          onChange={setMailingAddress}
          required={false}
          labels={{
            line1: t('customers.addCustomer.line1'),
            line2: t('customers.addCustomer.line2'),
            city: t('customers.addCustomer.city'),
            state: t('customers.addCustomer.state'),
            postalCode: t('customers.addCustomer.postalCode'),
          }}
        />
      </div>

      <div className="block text-sm">
        {record?.taxIdLast4 && !clearTaxId ? (
          <p className="mb-2 flex items-center gap-2 text-sm">
            <span>{t('legalEntity.taxIdOnFile', { last4: record.taxIdLast4 })}</span>
            <button
              type="button"
              className="text-xs text-[var(--color-destructive)] underline underline-offset-2"
              onClick={() => setClearTaxId(true)}
            >
              {t('legalEntity.taxIdClear')}
            </button>
          </p>
        ) : null}
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('legalEntity.taxId')}</span>
          <input
            className={FIELD_CLASS}
            value={taxId}
            placeholder={t('legalEntity.taxIdPlaceholder')}
            onChange={(event) => {
              setTaxId(event.target.value);
              if (event.target.value) {
                setClearTaxId(false);
              }
            }}
          />
        </label>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{t('legalEntity.taxIdHint')}</p>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('legalEntity.notes')}</span>
        <textarea
          rows={2}
          className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {isEditing ? (
        <div className="space-y-2 border-t border-[var(--color-border)] pt-4">
          <h3 className="text-sm font-semibold">{t('legalEntity.relatedPeopleHeading')}</h3>
          {record && record.relationships.length > 0 ? (
            <ul className="space-y-1">
              {record.relationships.map((relationship) => (
                <li
                  key={relationship.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-2 py-1.5 text-sm"
                >
                  <span>
                    {relationship.personName} — {relationship.roleLabel}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-[var(--color-destructive)] underline underline-offset-2"
                    onClick={() => unlinkRelationship.mutate(relationship.id)}
                  >
                    {t('legalEntity.removeRelationship')}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--color-muted-foreground)]">{t('legalEntity.noRelatedPeople')}</p>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <span className="mb-1 block text-xs font-medium">{t('legalEntity.addPerson')}</span>
              <PartyPicker
                kind="Person"
                ariaLabel={t('legalEntity.addPerson')}
                onSelect={(person) => {
                  setRelationshipError('');
                  if (!relationshipRoleId) {
                    setRelationshipError(t('legalEntity.pickARoleFirst'));
                    return;
                  }
                  linkRelationship.mutate(
                    { personPartyId: person.partyId, roleId: relationshipRoleId, notes: null },
                    {
                      onError: (error) => {
                        setRelationshipError(
                          error instanceof DuplicateRelationshipError
                            ? error.message
                            : t('legalEntity.relationshipFailed'),
                        );
                      },
                    },
                  );
                }}
              />
            </div>
            <label className="block text-xs">
              <span className="mb-1 block font-medium">{t('legalEntity.role')}</span>
              <select
                className={FIELD_CLASS}
                value={relationshipRoleId}
                onChange={(event) => setRelationshipRoleId(event.target.value)}
              >
                <option value="">{t('legalEntity.chooseRole')}</option>
                {(roles.data ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {relationshipError ? (
            <p role="alert" className="text-xs text-[var(--color-destructive)]">
              {relationshipError}
            </p>
          ) : null}
        </div>
      ) : null}

      {createEntity.isError || updateEntity.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('legalEntity.saveFailed')}
        </p>
      ) : null}
    </Dialog>
  );
}
